<template>
  <div class="ut-tree" aria-label="Estructura del curso">
    <!-- Add unit button -->
    <q-btn
      flat
      no-caps
      dense
      icon="add"
      label="Agregar unidad"
      class="ut-add-btn"
      @click="startAddUnit"
    />

    <!-- Inline create unit input -->
    <div v-if="addingUnit" class="ut-inline-create">
      <q-input
        ref="newUnitInputRef"
        v-model="newUnitTitle"
        outlined
        dense
        placeholder="Nombre de la unidad"
        class="ut-inline-input"
        @keydown.enter.prevent="saveNewUnit"
        @keydown.esc.prevent="cancelAddUnit"
      />
      <q-btn
        flat
        dense
        round
        icon="check"
        color="positive"
        :loading="creatingUnit"
        aria-label="Guardar unidad"
        @click="saveNewUnit"
      />
      <q-btn
        flat
        dense
        round
        icon="close"
        color="grey-6"
        aria-label="Cancelar"
        @click="cancelAddUnit"
      />
    </div>

    <!-- Units list -->
    <div
      v-for="(unit, unitIdx) in localUnits"
      :key="unit.id"
      class="ut-unit-block"
    >
      <!-- Inline unit title editing -->
      <div v-if="editingUnitId === unit.id" class="ut-inline-create">
        <q-input
          v-model="editUnitTitle"
          outlined
          dense
          placeholder="Nombre de la unidad"
          class="ut-inline-input"
          @keydown.enter.prevent="saveEditUnit(unit)"
          @keydown.esc.prevent="cancelEditUnit"
        />
        <q-btn
          flat
          dense
          round
          icon="check"
          color="positive"
          :loading="savingUnit"
          aria-label="Guardar cambio"
          @click="saveEditUnit(unit)"
        />
        <q-btn
          flat
          dense
          round
          icon="close"
          color="grey-6"
          aria-label="Cancelar"
          @click="cancelEditUnit"
        />
      </div>

      <!-- Unit row -->
      <UnitRow
        v-else
        :unit="unit"
        :index="unitIdx"
        :unit-number="unitIdx + 1"
        :is-first="unitIdx === 0"
        :is-last="unitIdx === localUnits.length - 1"
        :is-dragging="dragState.draggingId.value === unit.id"
        @edit="startEditUnit(unit)"
        @delete="confirmDeleteUnit(unit)"
        @move="handleMoveUnit"
        @drag-start="dragState.onDragStart"
        @drag-over="dragState.onDragOver"
        @drop="dragState.onDrop"
        @drag-end="dragState.onDragEnd"
      />

      <!-- Lessons under this unit -->
      <div class="ut-lessons">
        <!-- Empty state -->
        <p v-if="unit.lessons.length === 0 && !addingLessonUnitId" class="ut-empty-lessons">
          Esta unidad no tiene lecciones. Agregá la primera.
        </p>

        <LessonRow
          v-for="(lesson, lessonIdx) in unit.lessons"
          :key="lesson.id"
          :lesson="lesson"
          :course-id="courseId"
          :index="lessonIdx"
          :is-first="lessonIdx === 0"
          :is-last="lessonIdx === unit.lessons.length - 1"
          :is-dragging="lessonDragState[unit.id]?.draggingId.value === lesson.id"
          :is-active="activeLessonId === lesson.id"
          @edit="startEditLesson(lesson)"
          @delete="confirmDeleteLesson(lesson, unit)"
          @move="(from, to) => handleMoveLesson(unit, from, to)"
          @open="(l) => $emit('open-lesson', l)"
          @drag-start="(id) => getLessonDragState(unit.id).onDragStart(id)"
          @drag-over="(idx) => getLessonDragState(unit.id).onDragOver(idx)"
          @drop="() => getLessonDragState(unit.id).onDrop()"
          @drag-end="() => getLessonDragState(unit.id).onDragEnd()"
        />

        <!-- Inline create lesson input -->
        <div v-if="addingLessonUnitId === unit.id" class="ut-inline-create ut-inline-create--lesson">
          <q-input
            :ref="(el) => { if (el) newLessonInputRef = el as InstanceType<typeof import('quasar').QInput> }"
            v-model="newLessonTitle"
            outlined
            dense
            placeholder="Nombre de la lección"
            class="ut-inline-input"
            @keydown.enter.prevent="saveNewLesson(unit)"
            @keydown.esc.prevent="cancelAddLesson"
          />
          <q-btn
            flat
            dense
            round
            icon="check"
            color="positive"
            :loading="creatingLesson"
            aria-label="Guardar lección"
            @click="saveNewLesson(unit)"
          />
          <q-btn
            flat
            dense
            round
            icon="close"
            color="grey-6"
            aria-label="Cancelar"
            @click="cancelAddLesson"
          />
        </div>

        <!-- Add lesson button -->
        <q-btn
          flat
          no-caps
          dense
          icon="add"
          label="Agregar lección"
          class="ut-add-lesson-btn"
          @click="startAddLesson(unit.id)"
        />
      </div>
    </div>

    <!-- Delete unit dialog -->
    <q-dialog v-model="deleteUnitDialog" persistent>
      <q-card style="min-width: 320px; max-width: 420px;">
        <q-card-section>
          <div style="font-size: 20px; font-weight: 600; color: var(--oa-text-primary); margin-bottom: 8px;">
            Eliminar unidad
          </div>
          <p style="font-size: 15px; color: var(--oa-text-secondary); margin: 0;">
            Se eliminarán también todas sus lecciones. ¿Continuar?
          </p>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn
            flat
            no-caps
            label="Cancelar"
            color="grey-7"
            v-close-popup
          />
          <q-btn
            unelevated
            no-caps
            label="Eliminar"
            :loading="deletingUnit"
            style="background: var(--color-coral, #e05a3a); color: #fff;"
            @click="executeDeleteUnit"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Delete lesson dialog -->
    <q-dialog v-model="deleteLessonDialog" persistent>
      <q-card style="min-width: 320px; max-width: 420px;">
        <q-card-section>
          <div style="font-size: 20px; font-weight: 600; color: var(--oa-text-primary); margin-bottom: 8px;">
            Eliminar lección
          </div>
          <p style="font-size: 15px; color: var(--oa-text-secondary); margin: 0;">
            Se borrará el contenido de la lección. ¿Continuar?
          </p>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn
            flat
            no-caps
            label="Cancelar"
            color="grey-7"
            v-close-popup
          />
          <q-btn
            unelevated
            no-caps
            label="Eliminar"
            :loading="deletingLesson"
            style="background: var(--color-coral, #e05a3a); color: #fff;"
            @click="executeDeleteLesson"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, nextTick, watch } from 'vue';
import { useQuasar, type QInput } from 'quasar';
import { useRouter } from 'vue-router';
import { useDragSort } from 'src/composables/useDragSort';
import { useAdminCoursesStore, type AdminUnit, type AdminLesson } from 'stores/useAdminCoursesStore';
import UnitRow from './UnitRow.vue';
import LessonRow from './LessonRow.vue';

const props = defineProps<{
  courseId: string;
  units: AdminUnit[];
  activeLessonId?: string;
}>();

const emit = defineEmits<{
  (e: 'open-lesson', lesson: AdminLesson): void;
}>();

const $q = useQuasar();
const router = useRouter();
const adminStore = useAdminCoursesStore();

// =========================================================================
// Local unit list (optimistic)
// =========================================================================
const localUnits = ref<AdminUnit[]>(JSON.parse(JSON.stringify(props.units)));

// Sync with parent when props change (store mutations flow here via deep watch)
watch(
  () => props.units,
  (newUnits) => {
    localUnits.value = JSON.parse(JSON.stringify(newUnits)) as AdminUnit[];
  },
  { deep: true },
);

// =========================================================================
// Unit drag sort
// =========================================================================
const dragState = useDragSort(
  () => localUnits.value,
  async (reordered) => {
    const previous = [...localUnits.value];
    // Renormalize orderIndex (Pitfall 4)
    const normalized = reordered.map((u, idx) => ({ ...u, orderIndex: idx }));
    localUnits.value = normalized;

    try {
      await adminStore.reorderUnits(
        props.courseId,
        normalized.map((u) => ({ id: u.id, orderIndex: u.orderIndex })),
      );
    } catch {
      localUnits.value = previous;
      $q.notify({ type: 'negative', message: 'No se pudo reordenar. Revisá tu conexión e intentalo de nuevo.' });
    }
  },
);

async function handleMoveUnit(from: number, to: number) {
  if (to < 0 || to >= localUnits.value.length) return;

  const previous = [...localUnits.value];
  const arr = [...localUnits.value];
  const [item] = arr.splice(from, 1);
  arr.splice(to, 0, item);

  const normalized = arr.map((u, idx) => ({ ...u, orderIndex: idx }));
  localUnits.value = normalized;

  try {
    await adminStore.reorderUnits(
      props.courseId,
      normalized.map((u) => ({ id: u.id, orderIndex: u.orderIndex })),
    );
  } catch {
    localUnits.value = previous;
    $q.notify({ type: 'negative', message: 'No se pudo reordenar. Revisá tu conexión e intentalo de nuevo.' });
  }
}

// =========================================================================
// Per-unit lesson drag sort
// =========================================================================
const lessonDragState = reactive<
  Record<string, ReturnType<typeof useDragSort<AdminLesson>>>
>({});

function getLessonDragState(unitId: string) {
  if (!lessonDragState[unitId]) {
    lessonDragState[unitId] = useDragSort(
      () => localUnits.value.find((u) => u.id === unitId)?.lessons ?? [],
      async (reordered) => {
        const unitIdx = localUnits.value.findIndex((u) => u.id === unitId);
        if (unitIdx === -1) return;

        const previous = JSON.parse(JSON.stringify(localUnits.value)) as AdminUnit[];
        const normalized = reordered.map((l, idx) => ({ ...l, orderIndex: idx }));

        localUnits.value[unitIdx] = {
          ...localUnits.value[unitIdx],
          lessons: normalized,
        };

        try {
          await adminStore.reorderLessons(
            unitId,
            normalized.map((l) => ({ id: l.id, orderIndex: l.orderIndex })),
          );
        } catch {
          localUnits.value = previous;
          $q.notify({ type: 'negative', message: 'No se pudo reordenar. Revisá tu conexión e intentalo de nuevo.' });
        }
      },
    );
  }
  return lessonDragState[unitId];
}

async function handleMoveLesson(unit: AdminUnit, from: number, to: number) {
  if (to < 0 || to >= unit.lessons.length) return;

  const unitIdx = localUnits.value.findIndex((u) => u.id === unit.id);
  if (unitIdx === -1) return;

  const previous = JSON.parse(JSON.stringify(localUnits.value)) as AdminUnit[];
  const arr = [...localUnits.value[unitIdx].lessons];
  const [item] = arr.splice(from, 1);
  arr.splice(to, 0, item);

  const normalized = arr.map((l, idx) => ({ ...l, orderIndex: idx }));
  localUnits.value[unitIdx] = { ...localUnits.value[unitIdx], lessons: normalized };

  try {
    await adminStore.reorderLessons(
      unit.id,
      normalized.map((l) => ({ id: l.id, orderIndex: l.orderIndex })),
    );
  } catch {
    localUnits.value = previous;
    $q.notify({ type: 'negative', message: 'No se pudo reordenar. Revisá tu conexión e intentalo de nuevo.' });
  }
}

// =========================================================================
// Add unit
// =========================================================================
const addingUnit = ref(false);
const newUnitTitle = ref('');
const newUnitInputRef = ref<InstanceType<typeof QInput> | null>(null);
const creatingUnit = ref(false);

function startAddUnit() {
  addingUnit.value = true;
  newUnitTitle.value = '';
  void nextTick(() => {
    newUnitInputRef.value?.focus();
  });
}

function cancelAddUnit() {
  addingUnit.value = false;
  newUnitTitle.value = '';
}

async function saveNewUnit() {
  const title = newUnitTitle.value.trim();
  if (!title) return;

  creatingUnit.value = true;
  try {
    // Store pushes to editingCourse.units; the deep watch on props.units syncs localUnits.
    // No manual push here — that was the source of the duplicate.
    await adminStore.createUnit(props.courseId, title);
    cancelAddUnit();
  } catch {
    $q.notify({ type: 'negative', message: 'No se pudo crear la unidad. Revisá tu conexión e intentalo de nuevo.' });
  } finally {
    creatingUnit.value = false;
  }
}

// =========================================================================
// Edit unit
// =========================================================================
const editingUnitId = ref<string | null>(null);
const editUnitTitle = ref('');
const savingUnit = ref(false);

function startEditUnit(unit: AdminUnit) {
  editingUnitId.value = unit.id;
  editUnitTitle.value = unit.title;
}

function cancelEditUnit() {
  editingUnitId.value = null;
  editUnitTitle.value = '';
}

async function saveEditUnit(unit: AdminUnit) {
  const title = editUnitTitle.value.trim();
  if (!title) return;

  savingUnit.value = true;
  try {
    await adminStore.updateUnit(unit.id, title);
    const idx = localUnits.value.findIndex((u) => u.id === unit.id);
    if (idx !== -1) localUnits.value[idx] = { ...localUnits.value[idx], title };
    cancelEditUnit();
  } catch {
    $q.notify({ type: 'negative', message: 'No se pudo actualizar la unidad.' });
  } finally {
    savingUnit.value = false;
  }
}

// =========================================================================
// Delete unit
// =========================================================================
const deleteUnitDialog = ref(false);
const pendingDeleteUnit = ref<AdminUnit | null>(null);
const deletingUnit = ref(false);

function confirmDeleteUnit(unit: AdminUnit) {
  pendingDeleteUnit.value = unit;
  deleteUnitDialog.value = true;
}

async function executeDeleteUnit() {
  if (!pendingDeleteUnit.value) return;
  deletingUnit.value = true;

  const unitId = pendingDeleteUnit.value.id;
  const previous = [...localUnits.value];

  // Optimistic remove + renormalize
  localUnits.value = localUnits.value
    .filter((u) => u.id !== unitId)
    .map((u, idx) => ({ ...u, orderIndex: idx }));

  try {
    await adminStore.deleteUnit(unitId);
    deleteUnitDialog.value = false;
    pendingDeleteUnit.value = null;
  } catch {
    localUnits.value = previous;
    $q.notify({ type: 'negative', message: 'No se pudo eliminar la unidad.' });
    deleteUnitDialog.value = false;
  } finally {
    deletingUnit.value = false;
  }
}

// =========================================================================
// Add lesson
// =========================================================================
const addingLessonUnitId = ref<string | null>(null);
const newLessonTitle = ref('');
let newLessonInputRef: InstanceType<typeof QInput> | null = null;
const creatingLesson = ref(false);

function startAddLesson(unitId: string) {
  addingLessonUnitId.value = unitId;
  newLessonTitle.value = '';
  void nextTick(() => {
    newLessonInputRef?.focus();
  });
}

function cancelAddLesson() {
  addingLessonUnitId.value = null;
  newLessonTitle.value = '';
}

async function saveNewLesson(unit: AdminUnit) {
  const title = newLessonTitle.value.trim();
  if (!title) return;

  creatingLesson.value = true;
  try {
    // Store pushes to editingCourse.units[unit].lessons; the deep watch on props.units syncs localUnits.
    // No manual push here — that was the source of the duplicate.
    await adminStore.createLesson(unit.id, title);
    cancelAddLesson();
  } catch {
    $q.notify({ type: 'negative', message: 'No se pudo crear la lección. Revisá tu conexión e intentalo de nuevo.' });
  } finally {
    creatingLesson.value = false;
  }
}

// =========================================================================
// Edit lesson — navigate to the lesson editor
// =========================================================================
function startEditLesson(lesson: AdminLesson) {
  void router.push({
    name: 'admin-lesson-editor',
    params: { id: props.courseId, lid: lesson.id },
  });
}

// =========================================================================
// Delete lesson
// =========================================================================
const deleteLessonDialog = ref(false);
const pendingDeleteLesson = ref<{ lesson: AdminLesson; unit: AdminUnit } | null>(null);
const deletingLesson = ref(false);

function confirmDeleteLesson(lesson: AdminLesson, unit: AdminUnit) {
  pendingDeleteLesson.value = { lesson, unit };
  deleteLessonDialog.value = true;
}

async function executeDeleteLesson() {
  if (!pendingDeleteLesson.value) return;
  deletingLesson.value = true;

  const { lesson, unit } = pendingDeleteLesson.value;
  const unitIdx = localUnits.value.findIndex((u) => u.id === unit.id);
  if (unitIdx === -1) { deletingLesson.value = false; return; }

  const previousLessons = [...localUnits.value[unitIdx].lessons];

  // Optimistic remove + renormalize
  localUnits.value[unitIdx] = {
    ...localUnits.value[unitIdx],
    lessons: localUnits.value[unitIdx].lessons
      .filter((l) => l.id !== lesson.id)
      .map((l, idx) => ({ ...l, orderIndex: idx })),
  };

  try {
    await adminStore.deleteLesson(lesson.id);
    deleteLessonDialog.value = false;
    pendingDeleteLesson.value = null;
  } catch {
    localUnits.value[unitIdx] = { ...localUnits.value[unitIdx], lessons: previousLessons };
    $q.notify({ type: 'negative', message: 'No se pudo eliminar la lección.' });
    deleteLessonDialog.value = false;
  } finally {
    deletingLesson.value = false;
  }
}

// suppress unused
void emit;
</script>

<style scoped>
.ut-tree {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ut-add-btn {
  color: var(--color-ocean-light, #2e7fb5);
  font-size: 14px;
  margin-bottom: 8px;
  align-self: flex-start;
}

.ut-unit-block {
  margin-bottom: 4px;
}

.ut-lessons {
  padding-left: 0;
}

.ut-empty-lessons {
  font-size: 13px;
  color: var(--oa-text-muted);
  padding: 6px 8px 6px 28px;
  margin: 0;
}

.ut-add-lesson-btn {
  color: var(--color-ocean-light, #2e7fb5);
  font-size: 13px;
  padding-left: 28px;
  margin-top: 2px;
}

/* Inline create row */
.ut-inline-create {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  margin-bottom: 4px;
}

.ut-inline-create--lesson {
  padding-left: 28px;
}

.ut-inline-input {
  flex: 1;
}
</style>
